import { TFunction } from 'i18next';
import { refinePostalCode } from '@modules/settings/payment-methods/utils/schema';
import { PHONE_NUMBER_REGEX } from '@shared/constants/regex';
import { REGEX } from '@shared/utils';
import z from 'zod';

export const numberUseCaseSchema = (t: TFunction, isAustralianPhoneNumber?: boolean, isUKphoneNumber?: boolean) =>
  z
    .object({
      useCase: z.enum(['individual', 'business']).optional(),
      phoneNumber: z
        .string()
        .trim()
        .optional()
        .refine((val) => !val || PHONE_NUMBER_REGEX.test(val), {
          message: t('please_provide_valid_phone_number'),
        }),

      businessPhoneNumber: z
        .string()
        .trim()
        .optional()
        .refine((val) => !val || PHONE_NUMBER_REGEX.test(val), {
          message: t('please_provide_valid_business_phone_number'),
        }),
      firstName: z.string().trim().optional(),
      lastName: z.string().trim().optional(),
      email: z.string().toLowerCase().trim().optional(),
      businessName: z.string().trim().optional(),
      registrationNumber: z.string().trim().optional(),
      websiteUrl: z.string().trim().optional(),
      personalLink: z.string().trim().optional(),
      address: z
        .object({
          suburb: z
            .string()
            .trim()
            .nonempty({ message: t('please_enter_your_suburb') }),
          state: z
            .string()
            .trim()
            .nonempty({ message: t('please_enter_your_state') }),
          zip: z.string().trim(),
          country: z
            .string()
            .trim()
            .nonempty({ message: t('please_enter_your_country') }),
        })
        .superRefine(refinePostalCode(t)),
    })
    .superRefine((data, ctx) => {
      if (data.useCase === 'business') {
        // Business validations
        if (!data.businessName?.trim()) {
          ctx.addIssue({
            path: ['businessName'],
            code: z.ZodIssueCode.custom,
            message: t('please_provide_business_name'),
          });
        }

        if (!data.registrationNumber?.trim()) {
          ctx.addIssue({
            path: ['registrationNumber'],
            code: z.ZodIssueCode.custom,
            message: t('please_provide_registration_number'),
          });
        }

        if (data.businessPhoneNumber && !PHONE_NUMBER_REGEX.test(data.businessPhoneNumber)) {
          ctx.addIssue({
            path: ['businessPhoneNumber'],
            code: z.ZodIssueCode.custom,
            message: t('please_provide_valid_business_phone_number'),
          });
        }

        if (!data.websiteUrl) {
          ctx.addIssue({
            path: ['websiteUrl'],
            code: z.ZodIssueCode.custom,
            message: t('please_provide_website_url'),
          });
        } else {
          try {
            new URL(data.websiteUrl);
          } catch {
            ctx.addIssue({
              path: ['websiteUrl'],
              code: z.ZodIssueCode.custom,
              message: t('please_provide_valid_website_url'),
            });
          }
        }
      } else {
        // Individual validations
        if (!data.firstName?.trim()) {
          ctx.addIssue({
            path: ['firstName'],
            code: z.ZodIssueCode.custom,
            message: t('please_enter_your_first_name'),
          });
        }

        if (!data.lastName?.trim()) {
          ctx.addIssue({
            path: ['lastName'],
            code: z.ZodIssueCode.custom,
            message: t('please_enter_your_last_name'),
          });
        }

        if (!data.email) {
          ctx.addIssue({
            path: ['email'],
            code: z.ZodIssueCode.custom,
            message: t('please_provide_email'),
          });
        } else {
          const emailCheck = z.string().email().safeParse(data.email);
          if (!emailCheck.success) {
            ctx.addIssue({
              path: ['email'],
              code: z.ZodIssueCode.custom,
              message: t('please_provide_valid_email'),
            });
          }
        }

        // Personal link required for australian phone numbers
        if (isAustralianPhoneNumber) {
          if (!data.personalLink) {
            ctx.addIssue({
              path: ['personalLink'],
              code: z.ZodIssueCode.custom,
              message: t('please_provide_personal_link'),
            });
          } else {
            if (!REGEX.company_website.test(data.personalLink)) {
              ctx.addIssue({
                path: ['personalLink'],
                code: z.ZodIssueCode.custom,
                message: t('please_provide_valid_personal_link'),
              });
            }
          }
        }

        if (isUKphoneNumber && !data.phoneNumber?.trim()) {
          ctx.addIssue({
            path: ['phoneNumber'],
            code: z.ZodIssueCode.custom,
            message: t('please_provide_phone_number'),
          });
        } else if (data.phoneNumber && !PHONE_NUMBER_REGEX.test(data.phoneNumber)) {
          ctx.addIssue({
            path: ['phoneNumber'],
            code: z.ZodIssueCode.custom,
            message: t('please_provide_valid_phone_number'),
          });
        }
      }
    });

export const requestNumberSchema = (t: TFunction, isAustralianPhoneNumber?: boolean, isUKphoneNumber?: boolean) =>
  z.object({
    friendlyname: z
      .string()
      .trim()
      .nonempty({ message: t('please_provide_your_friendly_name') }),
    agree: z.literal(true, {
      errorMap: () => ({ message: t('you_must_agree_to_continue') }),
    }),
    identityDocumentId: z.number().optional(),
    identityDocumentBack: z.number().optional(),
    addressDocumentId: z.number().optional(),
    useCase: numberUseCaseSchema(t, isAustralianPhoneNumber, isUKphoneNumber),
  });
